import cloneDeep from 'lodash/cloneDeep';
import { Dictionary } from 'ts-essentials';

type Configuration<
  TItem extends Dictionary<unknown>,
  TChildrenKey extends keyof TItem,
  TResultItem extends Dictionary<unknown> = Omit<TItem, TChildrenKey>,
> = {
  /**
   * The key that represents the nested items that will be used to extract and
   * flatten the items from.
   */
  readonly childrenKey: TChildrenKey;
  /** Defines the shape of result item based on the current item and the path. */
  readonly transformItem?: (item: TItem, path: readonly TItem[]) => TResultItem;
};

/**
 * Creates a flat list of items from a tree of nested items.
 *
 * @example
 *   const input = [
 *     {
 *       id: 'a',
 *       label: 'A',
 *       items: [
 *         {
 *           id: 'b',
 *           label: 'B',
 *           parent_id: 'a',
 *           items: [{ id: 'c', label: 'C', parent_id: 'b' }],
 *         },
 *       ],
 *     },
 *   ];
 *
 *   convertTreeToFlatItems(input, { childrenKey: 'items' });
 *
 *   const output = [
 *     { id: 'a', label: 'A' },
 *     { id: 'b', label: 'B', parent_id: 'a' },
 *     { id: 'c', label: 'C', parent_id: 'b' },
 *   ];
 *
 * @example
 *   const input = [
 *     {
 *       id: 'a',
 *       label: 'A',
 *       items: [
 *         {
 *           id: 'b',
 *           label: 'B',
 *           items: [{ id: 'c', label: 'C' }],
 *         },
 *       ],
 *     },
 *   ];
 *
 *   convertTreeToFlatItems(input, {
 *     childrenKey: 'items',
 *     transformItem: ({ items, ...item }, path) => ({
 *       ...item,
 *       ...(path.length ? { parent_id: path[path.length - 1].id } : {}),
 *     }),
 *   });
 *
 *   const output = [
 *     { id: 'a', label: 'A' },
 *     { id: 'b', label: 'B', parent_id: 'a' },
 *     { id: 'c', label: 'C', parent_id: 'b' },
 *   ];
 *
 * @param items The items to flatten.
 * @param config The configuration.
 */
export function convertTreeToFlatItems<
  TItem extends Dictionary<unknown>,
  TChildrenKey extends keyof TItem,
  TResultItem extends Dictionary<unknown> = Omit<TItem, TChildrenKey>,
>(
  items: readonly TItem[],
  config: Configuration<TItem, TChildrenKey, TResultItem>,
): readonly TResultItem[] {
  const {
    childrenKey,
    transformItem = (item) =>
      Object.fromEntries(
        Object.entries(item).filter(([key]) => key !== childrenKey),
      ) as TResultItem,
  } = config;
  const result: TResultItem[] = [];

  /**
   * Flattens the items.
   *
   * @param items The items to flatten.
   * @param parentIdValue The value of the parent id.
   */
  const flattenItems = (
    items: readonly TItem[],
    path: readonly TItem[] = [],
  ) => {
    items.forEach((item) => {
      result.push(transformItem(item, path));

      const childrenItems = item[childrenKey] as TItem[] | undefined;
      if (childrenItems?.length) {
        flattenItems(childrenItems, path.concat([item]));
      }
    });
  };

  flattenItems(cloneDeep(items));

  return result;
}
